// src/theme.js
import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#100331',
      light: '#C9A0DC',
      dark: '#0D0229',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#FF3749',
      light: '#FF6B78',
      dark: '#CC2C3A',
      contrastText: '#ffffff',
    },
    accent: {
      teal: '#23A38F',
      gold: '#D8A200',
      blue: '#0097B2',
    },
    grey: {
      500: '#5D5D5D',
      300: '#767676', // WCAG AA compliant (4.54:1 contrast ratio)
    },
    background: {
      default: '#ffffff',
      paper: '#F8F9FA',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)', // High contrast black
      secondary: 'rgba(0, 0, 0, 0.6)', // Medium contrast black
      disabled: 'rgba(0, 0, 0, 0.38)', // Low contrast (still readable)
    },
  },
  typography: {
    fontFamily: [
      'DM Sans',
      'sans-serif',
    ].join(','),
    h1: {
      fontFamily: 'DM Sans, sans-serif',
      fontWeight: 700,
    },
    h2: {
      fontFamily: 'DM Sans, sans-serif',
      fontWeight: 700,
    },
    h3: {
      fontFamily: 'DM Sans, sans-serif',
      fontWeight: 700,
    },
    h4: {
      fontFamily: 'DM Sans, sans-serif',
      fontWeight: 700,
    },
    h5: {
      fontFamily: 'DM Sans, sans-serif',
      fontWeight: 700,
    },
    h6: {
      fontFamily: 'DM Sans, sans-serif',
      fontWeight: 700,
    },
    subtitle1: {
      fontFamily: 'DM Sans, sans-serif',
    },
    subtitle2: {
      fontFamily: 'DM Sans, sans-serif',
    },
    body1: {
      fontFamily: 'DM Sans, sans-serif',
    },
    body2: {
      fontFamily: 'DM Sans, sans-serif',
    },
    button: {
      fontFamily: 'DM Sans, sans-serif',
      fontWeight: 600,
    },
    caption: {
      fontFamily: 'DM Sans, sans-serif',
    },
    overline: {
      fontFamily: 'DM Sans, sans-serif',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#100331',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 'bold',
          borderRadius: 8,
        },
        containedPrimary: {
          backgroundColor: '#FF3749',
          '&:hover': {
            backgroundColor: '#CC2C3A',
          },
        },
        outlinedPrimary: {
          borderColor: '#FF3749',
          color: '#FF3749',
          '&:hover': {
            backgroundColor: 'rgba(255, 55, 73, 0.04)',
            borderColor: '#CC2C3A',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#100331',
          color: '#ffffff',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#C9A0DC',
          // Override for menu items
          '.MuiMenuItem-root &': {
            color: '#5D5D5D', // Grey for menu icons
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: '#ffffff',
          // Override for menu items
          '.MuiMenuItem-root &': {
            color: '#212121', // Very dark gray for menu text
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: '#FF3749',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#F8F9FA',
          color: '#212121', // Very dark gray for better readability
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#212121', // Very dark gray text
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 55, 73, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(255, 55, 73, 0.12)',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // Ensure all text in Paper components has proper contrast
          color: 'rgba(0, 0, 0, 0.87)', // High contrast black text
          '& .MuiTypography-root': {
            // Override any Typography without explicit color
            '&:not([class*="color"])': {
              color: 'inherit',
            },
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          // Default to high contrast if no color is specified
          '&:not([class*="color"])': {
            color: 'rgba(0, 0, 0, 0.87)',
          },
        },
      },
    },
  },
});

// We load the fonts directly in the head instead of using FontFace API
// because the FontFace API requires additional handling that may not be needed

// Try to load the fonts (this will work in a browser environment)
if (typeof document !== 'undefined') {
  // Add a stylesheet for the Google Fonts
  const link = document.createElement('link');
  link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Caveat:wght@700&display=swap';
  link.rel = 'stylesheet';
  document.head.appendChild(link);
}

export default theme;